import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, MapPin, Briefcase, Building2, Clock, DollarSign, Users, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api, { getAssetUrl, formatSalary } from '@/utils/api';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import CompanyAvatar from '@/components/CompanyAvatar';

const JOBS_PER_PAGE = 20;

const Home = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [recentEmployers, setRecentEmployers] = useState([]);
  const [clientLogos, setClientLogos] = useState([]);
  const [quickStats, setQuickStats] = useState({ totalJobs: 0, activeEmployers: 0, primeEmployers: 0 });
  const [heroBanner, setHeroBanner] = useState('https://customer-assets.emergentagent.com/job_talent-network-10/artifacts/jcrn72ip_1768327980934.jpg');
  const [filters, setFilters] = useState({
    search_query: '',  // Combined: Job Title, Skills, Companies
    location: '',
    min_experience: ''
  });
  // Applied filters - these are the filters actually used for search
  const [appliedFilters, setAppliedFilters] = useState({
    search_query: '',
    location: '',
    min_experience: ''
  });
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Location suggestion states
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);

  // Experience options
  const experienceOptions = [
    { value: '', label: 'Min Experience' },
    { value: '0', label: '0 Year - Fresher' },
    { value: '1', label: '1 Year' },
    { value: '2', label: '2 Years' },
    { value: '3', label: '3 Years' },
    { value: '4', label: '4 Years' },
    { value: '5', label: '5 Years' },
    { value: '6', label: '6 Years' },
    { value: '7', label: '7 Years' },
    { value: '8', label: '8 Years' },
    { value: '9', label: '9 Years' },
    { value: '10', label: '10 Years' },
    { value: '12', label: '12 Years' },
    { value: '15', label: '15 Years' },
    { value: '20', label: '20+ Years' },
  ];

  // Fetch jobs using applied filters (not the form input filters)
  const fetchJobs = async (searchFilters = appliedFilters) => {
    try {
      const params = new URLSearchParams({ page, limit: JOBS_PER_PAGE });
      if (searchFilters.search_query) {
        params.append('search', searchFilters.search_query);
      }
      if (searchFilters.location) {
        params.append('location', searchFilters.location);
      }
      // Only add min_experience if it's a valid number (not empty, not 'all')
      if (searchFilters.min_experience && searchFilters.min_experience !== 'all') {
        params.append('min_experience', searchFilters.min_experience);
      }
      const response = await api.get(`/jobs?${params}`);
      setJobs(response.data.jobs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchRecentEmployers = async () => {
    try {
      // Fetch active approved jobs only (API already filters for approved/active)
      const response = await api.get('/jobs?limit=100');
      const allJobs = response.data.jobs || [];
      
      // Get unique employers with their logos from recent jobs
      // API already returns only approved and active jobs, so no need to filter
      const employerMap = new Map();
      allJobs.forEach(job => {
        const key = job.is_prime_employer_job ? `prime-${job.prime_employer_id}` : `emp-${job.employer_id}`;
        if (!employerMap.has(key)) {
          employerMap.set(key, {
            id: key,
            company_name: job.company_name,
            logo: job.company_logo,
            job_count: 1,
            latest_job_date: job.created_at,
            is_prime: job.is_prime_employer_job || false
          });
        } else {
          const existing = employerMap.get(key);
          existing.job_count += 1;
        }
      });
      
      // Also fetch prime employers directly to get their logos for existing prime jobs
      try {
        const primeResponse = await api.get('/admin/prime-employers?limit=20');
        const primeEmployers = primeResponse.data.prime_employers || [];
        primeEmployers.forEach(pe => {
          const key = `prime-${pe.id}`;
          // Only update logo for existing prime employers that already have jobs
          // Don't add new entries - only employers with active jobs should show
          if (employerMap.has(key)) {
            const existing = employerMap.get(key);
            if (!existing.logo && pe.logo) {
              existing.logo = pe.logo;
            }
          }
        });
      } catch (e) {
        console.log('Could not fetch prime employers');
      }
      
      // Sort by latest job date and take top employers - only show those with jobs
      const employers = Array.from(employerMap.values())
        .filter(e => e.job_count > 0)
        .sort((a, b) => new Date(b.latest_job_date) - new Date(a.latest_job_date))
        .slice(0, 16);
      
      setRecentEmployers(employers);
    } catch (error) {
      console.error('Error fetching employers:', error);
    }
  };

  const fetchClientLogos = async () => {
    try {
      const response = await api.get('/client-logos');
      setClientLogos(response.data.logos || []);
    } catch (error) {
      console.error('Error fetching client logos:', error);
    }
  };

  const fetchQuickStats = async () => {
    try {
      const response = await api.get('/home/quick-stats');
      setQuickStats({
        totalJobs: response.data.total_jobs || 0,
        activeEmployers: response.data.active_employers || 0,
        primeEmployers: response.data.prime_employers || 0
      });
    } catch (error) {
      console.error('Error fetching quick stats:', error);
    }
  };

  const fetchHeroBanner = async () => {
    try {
      const response = await api.get('/cms/hero-banner');
      if (response.data.hero_image) {
        setHeroBanner(response.data.hero_image);
      }
    } catch (error) {
      console.error('Error fetching hero banner:', error);
    }
  };

  // Location suggestion search
  const searchLocationSuggestions = async (query) => {
    if (query.length < 3) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }
    
    try {
      const response = await api.get(`/location-master/locations/search?q=${encodeURIComponent(query)}&limit=10`);
      setLocationSuggestions(response.data);
      setShowLocationSuggestions(response.data.length > 0);
    } catch (error) {
      console.error('Error searching locations:', error);
      setLocationSuggestions([]);
    }
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setFilters({...filters, location: value});
    searchLocationSuggestions(value);
  };

  const selectLocationSuggestion = (suggestion) => {
    // Use city name if available, otherwise district
    const locationText = suggestion.city && suggestion.city !== suggestion.district 
      ? suggestion.city 
      : suggestion.district;
    setFilters({...filters, location: locationText});
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
  };

  const clearAllFilters = () => {
    const emptyFilters = {
      search_query: '',
      location: '',
      min_experience: ''
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSelectedIndustries([]);
    setSelectedRoles([]);
    setPage(1);
    // Fetch all jobs without filters
    fetchJobs(emptyFilters);
  };

  const hasActiveFilters = appliedFilters.search_query || appliedFilters.location || appliedFilters.min_experience || selectedIndustries.length > 0 || selectedRoles.length > 0;

  useEffect(() => {
    fetchJobs(appliedFilters);
    fetchRecentEmployers();
    fetchClientLogos();
    fetchQuickStats();
    fetchHeroBanner();
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    // Apply the current form filters
    setAppliedFilters({...filters});
    setPage(1);
    // Fetch jobs with current filters
    fetchJobs(filters);
  };

  const handleIndustryFilter = (industry) => {
    setSelectedIndustries(prev => 
      prev.includes(industry) 
        ? prev.filter(i => i !== industry)
        : [...prev, industry]
    );
  };

  const handleRoleFilter = (role) => {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const getDefaultLogo = () => 'https://images.pexels.com/photos/345132/pexels-photo-345132.jpeg?auto=compress&cs=tinysrgb&w=100&h=100';

  const getImageUrl = (path) => {
    if (!path) return getDefaultLogo();
    if (path.startsWith('http')) return path;
    return getAssetUrl(path);
  };

  // Filter jobs based on selected industries and roles
  const filteredJobs = jobs.filter(job => {
    if (selectedIndustries.length > 0) {
      const jobIndustry = job.industry_sector || job.industry_type || '';
      if (!selectedIndustries.some(ind => jobIndustry.toLowerCase().includes(ind.toLowerCase()))) {
        return false;
      }
    }
    if (selectedRoles.length > 0) {
      if (!selectedRoles.some(role => job.job_title.toLowerCase().includes(role.toLowerCase()))) {
        return false;
      }
    }
    return true;
  });

  const totalPages = Math.ceil(total / JOBS_PER_PAGE);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    let startPage = Math.max(1, page - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);
    
    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-slate-50 to-white flex flex-col">
      <SEO 
        title="VVVSI - Free Job Portal | Vallalar Velai Vaaipu Sevai Iyakkam"
        description="VVVSI - India's trusted free job portal. Find jobs, post vacancies, and build your career with Vallalar Velai Vaaipu Sevai Iyakkam. Connecting job seekers with employers across Tamil Nadu and India."
        keywords="VVVSI, Vallalar Velai Vaaipu Sevai Iyakkam, free job portal, jobs in Tamil Nadu, India jobs, employment, career, recruitment, job search, job fair, government jobs, private jobs"
        url="/"
      />
      <Header />

      {/* Mobile Quick Login Bar - Yellow Notification */}
      <div className="lg:hidden bg-gradient-to-r from-amber-400 to-yellow-400 px-4 py-3 shadow-md">
        <div className="flex items-center justify-center gap-3">
          <span className="text-amber-900 font-medium text-sm">Quick Access:</span>
          <Link 
            to="/job-seeker/login"
            className="px-4 py-1.5 bg-white text-amber-700 rounded-full text-sm font-semibold hover:bg-amber-50 transition-colors shadow-sm"
          >
            Job Seeker Login
          </Link>
          <Link 
            to="/employer/login"
            className="px-4 py-1.5 bg-amber-800 text-white rounded-full text-sm font-semibold hover:bg-amber-900 transition-colors shadow-sm"
          >
            Employer Login
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1">
        {/* (Desktop hero moved to above-footer location for better job-search visibility) */}

        {/* Mobile: Compact intro text above search */}
        <div className="lg:hidden max-w-7xl mx-auto px-4 py-4">
          <h2 className="text-lg font-bold text-slate-900 text-center mb-2" style={{fontFamily: 'Outfit'}}>
            India's Trusted Job Portal with Zero Fees
          </h2>
          <div className="flex justify-center gap-4 text-xs text-slate-600 mb-2">
            <span><strong>2.17 Lakh+</strong> Placements</span>
            <span><strong>34,000+</strong> Employers</span>
          </div>
        </div>

        {/* Search Filters Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-4 sm:p-6 shadow-lg border-slate-200 mb-8 sm:mb-12 bg-gradient-to-br from-white to-slate-50">
            <form onSubmit={handleSearch}>
              {/* Search Fields Row - All in one line on desktop */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-3">
                {/* Job Title, Skills, Companies - Takes 4 columns on desktop */}
                <div className="md:col-span-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-teal-500" />
                    <Input
                      data-testid="search-query-input"
                      placeholder="Job Title, Skills, Companies..."
                      value={filters.search_query}
                      onChange={(e) => setFilters({...filters, search_query: e.target.value})}
                      className="h-12 pl-11 text-base border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                    />
                  </div>
                </div>

                {/* Location - Takes 3 columns on desktop */}
                <div className="md:col-span-3 relative">
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-teal-500" />
                    <Input
                      data-testid="search-location-input"
                      placeholder="State / District / City"
                      value={filters.location}
                      onChange={handleLocationChange}
                      onFocus={() => filters.location.length >= 3 && setShowLocationSuggestions(locationSuggestions.length > 0)}
                      onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 200)}
                      className="h-12 pl-11 text-base border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                    />
                  </div>
                  {showLocationSuggestions && locationSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                      {locationSuggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          type="button"
                          className="w-full px-4 py-3 text-left hover:bg-teal-50 flex items-center gap-3 text-sm border-b border-slate-100 last:border-b-0 transition-colors"
                          onMouseDown={() => selectLocationSuggestion(suggestion)}
                        >
                          <MapPin className="w-4 h-4 text-teal-500 flex-shrink-0" />
                          <span className="flex-1 text-slate-700">{suggestion.display}</span>
                          {suggestion.type === 'city' && (
                            <span className="text-xs px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full font-medium">City</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Experience - Takes 2 columns on desktop */}
                <div className="md:col-span-2">
                  <Select 
                    value={filters.min_experience || ''} 
                    onValueChange={(value) => {
                      const newValue = value === 'all' ? '' : value;
                      setFilters({...filters, min_experience: newValue});
                    }}
                  >
                    <SelectTrigger className="h-12 text-base border-slate-300 focus:border-teal-500 focus:ring-teal-500">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-teal-500 flex-shrink-0" />
                        <SelectValue placeholder="Min Exp" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {experienceOptions.map((opt) => (
                        <SelectItem key={opt.value || 'default'} value={opt.value || 'all'}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Buttons - Takes 3 columns on desktop, inline with fields */}
                <div className="md:col-span-3 flex gap-2">
                  <Button 
                    data-testid="search-jobs-btn" 
                    type="submit" 
                    className="h-12 px-6 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-base flex-1"
                  >
                    <Search className="w-5 h-5 mr-2" />
                    Search
                  </Button>
                  {hasActiveFilters && (
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={clearAllFilters}
                      className="h-12 px-4 border-slate-300 text-slate-600 hover:bg-slate-100 font-medium"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              {/* Active Filters Display - Shows only applied filters */}
              {hasActiveFilters && (
                <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-200">
                  <span className="text-sm text-slate-500">Active filters:</span>
                  {appliedFilters.search_query && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
                      <Search className="w-3 h-3" />
                      {appliedFilters.search_query}
                      <button type="button" onClick={() => {
                        const newFilters = {...appliedFilters, search_query: ''};
                        setFilters(newFilters);
                        setAppliedFilters(newFilters);
                        fetchJobs(newFilters);
                      }} className="ml-1 hover:text-teal-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {appliedFilters.location && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
                      <MapPin className="w-3 h-3" />
                      {appliedFilters.location}
                      <button type="button" onClick={() => {
                        const newFilters = {...appliedFilters, location: ''};
                        setFilters(newFilters);
                        setAppliedFilters(newFilters);
                        fetchJobs(newFilters);
                      }} className="ml-1 hover:text-teal-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {appliedFilters.min_experience && appliedFilters.min_experience !== 'all' && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
                      <Briefcase className="w-3 h-3" />
                      {experienceOptions.find(o => o.value === appliedFilters.min_experience)?.label || appliedFilters.min_experience}
                      <button type="button" onClick={() => {
                        const newFilters = {...appliedFilters, min_experience: ''};
                        setFilters(newFilters);
                        setAppliedFilters(newFilters);
                        fetchJobs(newFilters);
                      }} className="ml-1 hover:text-teal-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
              )}
            </form>
          </Card>
        </div>

        {/* Job Listings Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Job Listings - Full Width Grid */}
          <div className="space-y-6">
            {/* Clear Filters Button - Only show when filters are active */}
            {(selectedIndustries.length > 0 || selectedRoles.length > 0) && (
              <div className="flex items-center justify-end">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { setSelectedIndustries([]); setSelectedRoles([]); }}
                  className="text-teal-600"
                >
                  Clear Filters
                </Button>
              </div>
            )}
            
            {filteredJobs.length === 0 && (
              <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
                <Briefcase className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600">No jobs found. Try adjusting your filters.</p>
              </div>
            )}
            
            {/* Job Cards Grid - 1 col mobile, 2 cols tablet, 3 cols desktop */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {filteredJobs.map((job) => (
                <Card 
                  key={job.id} 
                  data-testid={`job-card-${job.id}`}
                  className="group bg-white p-5 sm:p-6 rounded-xl border border-slate-200 hover:border-teal-500/50 hover:shadow-xl transition-all duration-300 cursor-pointer"
                  onClick={() => navigate(`/job/${job.id}`)}
                >
                  {/* Header with Logo and Company */}
                  <div className="flex items-start gap-4 mb-4">
                    <CompanyAvatar 
                      name={job.company_name}
                      logoUrl={getImageUrl(job.company_logo)}
                      size="lg"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-slate-900 group-hover:text-teal-600 transition-colors line-clamp-2 mb-1" style={{fontFamily: 'Outfit'}}>
                        {job.job_title}
                      </h3>
                      <p className="text-sm text-slate-600 truncate flex items-center gap-1">
                        <Building2 className="w-4 h-4 flex-shrink-0" />
                        {job.company_name}
                      </p>
                    </div>
                  </div>
                  
                  {/* Job Type & Location */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
                      {job.job_type}
                    </span>
                    <span className="text-sm text-slate-600 flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {job.locations?.[0]?.city || 'Multiple Locations'}
                    </span>
                  </div>
                  
                  {/* Job Details Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-slate-50 px-3 py-2 rounded-lg">
                      <p className="text-xs text-slate-500 mb-0.5">Salary</p>
                      <p className="text-sm font-medium text-slate-900">{formatSalary(job)}</p>
                    </div>
                    <div className="bg-slate-50 px-3 py-2 rounded-lg">
                      <p className="text-xs text-slate-500 mb-0.5">Experience</p>
                      <p className="text-sm font-medium text-slate-900">{job.min_experience} - {job.max_experience} years</p>
                    </div>
                    {job.min_education && (
                      <div className="bg-purple-50 px-3 py-2 rounded-lg">
                        <p className="text-xs text-purple-600 mb-0.5">Education</p>
                        <p className="text-sm font-medium text-purple-900 truncate">{job.min_education}</p>
                      </div>
                    )}
                    <div className="bg-slate-50 px-3 py-2 rounded-lg">
                      <p className="text-xs text-slate-500 mb-0.5">Openings</p>
                      <p className="text-sm font-medium text-slate-900">{job.vacancies} positions</p>
                    </div>
                  </div>
                  
                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Posted {new Date(job.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                    <span className="text-sm font-medium text-teal-600 group-hover:text-teal-700">
                      View Details →
                    </span>
                  </div>
                </Card>
              ))}
            </div>
            
            {/* Pagination - Outside the grid */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                    className="flex items-center gap-1 px-4"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  
                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {getPageNumbers().map((pageNum) => (
                      <Button
                        key={pageNum}
                        variant={page === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        className={`w-10 ${page === pageNum ? 'bg-teal-600 hover:bg-teal-700' : ''}`}
                      >
                        {pageNum}
                      </Button>
                    ))}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                    className="flex items-center gap-1 px-4"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
                
                <p className="text-sm text-slate-500">
                  Showing {((page - 1) * JOBS_PER_PAGE) + 1} - {Math.min(page * JOBS_PER_PAGE, total)} of {total} jobs
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Job Posted Employers Section - Before Footer */}
        {recentEmployers.length > 0 && (
          <div className="bg-slate-50 py-12 mt-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-2 text-center" style={{fontFamily: 'Outfit'}}>
                Recent Job Posted Employers
              </h3>
              <p className="text-slate-600 text-center mb-8">Trusted companies actively hiring</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
                {recentEmployers.map((employer, index) => (
                  <Card 
                    key={employer.id || index}
                    data-testid={`employer-card-${index}`}
                    className="p-4 flex flex-col items-center justify-center hover:shadow-lg transition-all duration-300 cursor-pointer border-slate-100 hover:border-teal-500/30 bg-white"
                    onClick={() => {
                      navigate(`/employer-jobs?company=${encodeURIComponent(employer.company_name)}&employer_id=${encodeURIComponent(employer.id)}`);
                    }}
                  >
                    <CompanyAvatar 
                      name={employer.company_name}
                      logoUrl={getImageUrl(employer.logo)}
                      size="lg"
                      className="mb-3"
                    />
                    <p className="text-sm text-center text-slate-800 font-medium line-clamp-2 w-full" title={employer.company_name}>
                      {employer.company_name}
                    </p>
                    <div className="flex items-center gap-1 mt-2">
                      <span className="text-xs text-teal-600 font-medium">
                        {employer.job_count} job{employer.job_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Our Clients Section */}
        {clientLogos.length > 0 && (
          <div className="bg-white py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-2 text-center" style={{fontFamily: 'Outfit'}}>
                Our Clients
              </h3>
              <p className="text-slate-600 text-center mb-8">Trusted by 32,000+ Employers across India</p>
              <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-10 gap-4">
                {clientLogos.slice(0, 20).map((client, index) => (
                  <div 
                    key={index}
                    className="bg-slate-50 rounded-lg p-3 flex items-center justify-center aspect-square hover:bg-slate-100 transition-colors border border-slate-100"
                    title={client.name}
                  >
                    <img 
                      src={getAssetUrl(client.logo)} 
                      alt={client.name}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                ))}
              </div>
              <div className="text-center mt-8">
                <Link 
                  to="/our-clients" 
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors"
                >
                  View More Clients
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Desktop Hero Section - Above Footer */}
        <div className="hidden lg:block bg-gradient-to-br from-teal-50 to-slate-50 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
              <div className="flex flex-col justify-center">
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mb-5" style={{fontFamily: 'Outfit'}}>
                  An Employment Social Service Trust Committed to Direct Employer Jobs
                </h2>
                <p className="text-base text-slate-600 leading-relaxed mb-4">
                  We are India's first registered Employment Social Service Trust, dedicated to serving society through honest and transparent employment support. For over 10 years, we have worked with a single mission—to connect job seekers directly with genuine employers and help families achieve stable livelihoods.
                </p>
                <div className="flex flex-wrap gap-8 mb-4 text-base text-slate-600">
                  <span><strong className="text-slate-900">2.17 Lakh+</strong> Job Placements</span>
                  <span><strong className="text-slate-900">34,000+</strong> Trusted Employers</span>
                  <span><strong className="text-slate-900">10+</strong> Years of Service</span>
                </div>
                <p className="text-base text-slate-600 leading-relaxed mb-4">
                  We believe employment is a social responsibility, not a business. That is why we follow a strict ethical approach:
                </p>
                <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border-l-4 border-teal-500 p-4 rounded-r-lg">
                  <p className="text-lg font-semibold text-teal-800">No fees. No middlemen. No false promises.</p>
                  <p className="text-sm text-teal-700 mt-1">
                    Only verified, direct employer job opportunities that bring dignity, security, and hope to every job seeker.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <div className="relative w-full h-full min-h-[350px]">
                  <div className="absolute -inset-3 bg-gradient-to-br from-teal-500/20 via-teal-400/10 to-transparent rounded-3xl blur-lg"></div>
                  <div className="relative overflow-hidden rounded-3xl shadow-xl h-full">
                    <img
                      src={heroBanner}
                      alt="VVVSI Team - Namma Ooru Hero"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: 'center center' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Hero Section - Above Footer */}
        <div className="lg:hidden bg-gradient-to-br from-teal-50 to-slate-50 py-8">
          <div className="max-w-7xl mx-auto px-4">
            <div className="relative mb-6">
              <div className="absolute -inset-2 bg-gradient-to-br from-teal-500/20 via-teal-400/10 to-transparent rounded-2xl blur-lg"></div>
              <div className="relative overflow-hidden rounded-xl shadow-xl">
                <img 
                  src={heroBanner} 
                  alt="VVVSI Team - Namma Ooru Hero"
                  className="w-full h-48 object-cover"
                  style={{ objectPosition: 'center center' }}
                />
              </div>
            </div>
            <h2 className="text-xl font-bold text-slate-900 text-center mb-3" style={{fontFamily: 'Outfit'}}>
              An Employment Social Service Trust
            </h2>
            <p className="text-sm text-slate-600 text-center leading-relaxed mb-4">
              India's first registered Employment Social Service Trust, dedicated to serving society through honest and transparent employment support.
            </p>
            <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border-l-4 border-teal-500 p-3 rounded-r-lg">
              <p className="text-sm font-semibold text-teal-800 text-center">
                No fees. No middlemen. No false promises.
              </p>
            </div>
            <div className="flex justify-center gap-3 mt-4 text-xs text-slate-600">
              <span><strong>2.17 Lakh+</strong> Placements</span>
              <span><strong>34,000+</strong> Employers</span>
              <span><strong>10+</strong> Years</span>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Home;
